import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { addToBench, addToBenchCancel, attach, attachCancel, evolve, evolveCancel, setActiveBenchPosition } from '../Pokemon/actions';
import { selectAttaching, selectBenching, selectEvolving } from '../Pokemon/selectors';
import { selectBench, selectGameIsStarted, selectIsActivePlayer, selectPlayer } from '../ActiveGame/selectors';
import { retreat, retreatDone } from '../RetreatButton/actions';
import { selectRetreating } from '../RetreatButton/selectors';
import ConfirmButton from '../ConfirmButton';
import ActionableButton from '../ActionableButton';
import CardFace from '../Card/CardFace';
import { Slot } from '../Card/styled';
import { BENCH } from '../ActiveGame/contextMenuTypes';
import { setContextMenu } from '../ActiveGame/actions';
import usePickTarget from '../Picks/usePickTarget';
import { ATTACH_TARGET, BENCH_SPOT, EVOLVE_TARGET, selectBoardChoice, selectEvolveTargets, SWITCH_IN } from '../Board/selectors';

/** One of the five bench spots, at `position` 0–4. */
const BenchSlot = ({ userId, position }) => {
  const dispatch = useDispatch();
  const attaching = useSelector(selectAttaching);
  const bench = useSelector((state) => selectBench(state, { userId }));
  const benching = useSelector(selectBenching);
  const boardChoice = useSelector(selectBoardChoice);
  const gameIsStarted = useSelector(selectGameIsStarted);
  const isActivePlayer = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const player = useSelector(selectPlayer);
  const retreating = useSelector(selectRetreating);
  const evolveTargets = useSelector(selectEvolveTargets);
  const evolving = useSelector(selectEvolving);

  const benchCard = bench && bench.get('cards').find(card => card.get('order') === position);
  const pickTarget = usePickTarget(
    userId,
    position,
    benchCard ? benchCard.get('name', 'this Pokémon') : '',
    benchCard ? benchCard.get('energyAttachments') : undefined,
  );
  // Choices only ever land on your own side: during your turn, or while both players set up.
  const isMine = userId === player.getIn(['user', 'id']);
  const choosingHere = isMine && (isActivePlayer || !gameIsStarted);

  if (!benchCard) {
    if (choosingHere && boardChoice === BENCH_SPOT) {
      return (
        <ConfirmButton
          onConfirm={() => dispatch(addToBench(position))}
          onCancel={() => dispatch(addToBenchCancel())}
          disabled={benching}
          prompt="Put the Pokémon on your bench here?"
          plain
        >
          <Slot $targetable data-targetable="true">+</Slot>
        </ConfirmButton>
      );
    }

    return <Slot />;
  }

  const name = benchCard.get('name', 'this Pokémon');
  let onClick = () => {
    dispatch(setActiveBenchPosition(position));
    dispatch(setContextMenu(BENCH, userId));
  };
  let onCancel = null;
  let disabled = false;
  let prompt = null;

  if (choosingHere && boardChoice === ATTACH_TARGET) {
    onClick = () => dispatch(attach(position));
    onCancel = () => dispatch(attachCancel());
    disabled = attaching;
    prompt = `Attach the energy to ${name}?`;
  }

  if (choosingHere && boardChoice === EVOLVE_TARGET && evolveTargets.includes(position)) {
    onClick = () => dispatch(evolve(position));
    onCancel = () => dispatch(evolveCancel());
    disabled = evolving;
    prompt = `Evolve ${name}?`;
  }

  // A Trainer or Power can pick either player's Pokémon; the hook knows which.
  if (pickTarget) {
    onClick = pickTarget.onClick;
    onCancel = pickTarget.onCancel;
    disabled = pickTarget.busy;
    prompt = pickTarget.prompt;
  }

  if (choosingHere && boardChoice === SWITCH_IN) {
    onClick = () => dispatch(retreat(position));
    onCancel = () => dispatch(retreatDone());
    disabled = retreating;
    prompt = `Switch ${name} in as your active Pokémon?`;
  }

  return (
    <ActionableButton onClick={onClick} onCancel={onCancel} disabled={disabled} prompt={prompt} plain>
      <CardFace
        card={benchCard}
        damage={benchCard.get('damage')}
        energyAttachments={benchCard.get('energyAttachments')}
        trainers={benchCard.get('trainers')}
        targetable={!!onCancel && !disabled}
      />
    </ActionableButton>
  );
};

BenchSlot.propTypes = {
  userId: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
};

export default BenchSlot;
